import { useState, useEffect, useRef, useCallback } from "react"
import { csrfToken } from "@/lib/csrf"

interface LocationResult {
  id: string
  name: string
  address?: string
  verified?: boolean
  picture_url?: string
  hours?: string
}

export interface LocationMeta {
  address?: string
  verified?: boolean
  picture_url?: string
  hours?: string
}

interface LocationPickerProps {
  locationId: string | null
  locationName: string | null
  locationMeta?: LocationMeta | null
  onLocationChange: (id: string | null, name: string | null, meta?: LocationMeta) => void
  defaultQuery?: string
  lat?: number | string | null
  lng?: number | string | null
  disabled?: boolean
}

export default function LocationPicker({
  locationId,
  locationName,
  locationMeta,
  onLocationChange,
  defaultQuery,
  lat,
  lng,
  disabled,
}: LocationPickerProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<LocationResult[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const search = useCallback(
    async (q: string) => {
      if (q.trim().length < 2) {
        setResults([])
        setHasSearched(false)
        return
      }

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      try {
        const params = new URLSearchParams({ q: q.trim() })
        if (lat) params.set("lat", String(lat))
        if (lng) params.set("lng", String(lng))

        const response = await fetch(`/dashboard/location_search?${params}`, {
          headers: { "X-CSRF-Token": csrfToken() },
          signal: controller.signal,
        })

        if (response.ok) {
          const data: LocationResult[] = await response.json()
          setResults(data)
          setHasSearched(true)
          setShowDropdown(true)
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return
      } finally {
        setLoading(false)
      }
    },
    [lat, lng],
  )

  // Pre-fill query from defaultQuery and auto-search on mount
  const hasInitialised = useRef(false)
  useEffect(() => {
    if (!hasInitialised.current && defaultQuery && !locationId) {
      setQuery(defaultQuery)
      hasInitialised.current = true
      search(defaultQuery)
    }
  }, [defaultQuery, locationId, search])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  function handleInputChange(value: string) {
    setQuery(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(value), 300)
  }

  function selectLocation(loc: LocationResult) {
    onLocationChange(loc.id, loc.name, {
      address: loc.address,
      verified: loc.verified,
      picture_url: loc.picture_url,
      hours: loc.hours,
    })
    setQuery("")
    setResults([])
    setShowDropdown(false)
    setHasSearched(false)
  }

  function clearLocation() {
    onLocationChange(null, null)
  }

  if (disabled) {
    return (
      <div className="space-y-1.5 border-t border-stone/20 pt-4 dark:border-white/10">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey dark:text-cream/40">
          Location tag
        </p>
        <div className="flex items-center gap-2 rounded-md border border-stone/20 bg-stone/5 px-3 py-2 text-xs text-warm-grey/60 dark:border-white/10 dark:bg-white/5 dark:text-cream/30">
          <MapPinIcon className="size-3.5 shrink-0" />
          Connect Facebook or Instagram to tag locations
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="space-y-1.5 border-t border-stone/20 pt-4 dark:border-white/10">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey dark:text-cream/40">
        Location tag
      </p>

      {locationId ? (
        <SelectedLocation
          name={locationName}
          meta={locationMeta}
          onClear={clearLocation}
        />
      ) : (
        <div className="relative">
          <div className="relative">
            <MapPinIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-warm-grey/50" />
            <input
              type="text"
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={() => {
                if (results.length > 0) setShowDropdown(true)
              }}
              placeholder="Search for a location..."
              className="w-full rounded-md border border-stone/30 bg-white py-2 pl-8 pr-8 text-sm text-charcoal placeholder:text-warm-grey/50 focus:border-red/30 focus:outline-none focus:ring-1 focus:ring-red/20 dark:border-white/10 dark:bg-white/5 dark:text-cream dark:placeholder:text-cream/30"
            />
            {loading && (
              <svg className="absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-warm-grey/50" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>

          {showDropdown && (
            <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-stone/30 bg-white shadow-lg dark:border-white/10 dark:bg-charcoal">
              {results.length > 0 ? (
                <>
                  {results.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => selectLocation(loc)}
                      className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-stone/10 dark:hover:bg-white/5"
                    >
                      {loc.picture_url ? (
                        <img
                          src={loc.picture_url}
                          alt=""
                          className="mt-0.5 size-8 shrink-0 rounded-full border border-stone/20 object-cover"
                        />
                      ) : (
                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-stone/10">
                          <MapPinIcon className="size-4 text-warm-grey/50" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-charcoal dark:text-cream">{loc.name}</span>
                          {loc.verified && <VerifiedBadge />}
                        </div>
                        {loc.address && (
                          <p className="truncate text-[11px] text-warm-grey">{loc.address}</p>
                        )}
                        {loc.hours && (
                          <p className="truncate text-[11px] text-warm-grey/70">{loc.hours}</p>
                        )}
                      </div>
                    </button>
                  ))}
                  <div className="border-t border-stone/10 px-3 py-1.5 dark:border-white/5">
                    <p className="text-[10px] text-warm-grey/50">Source: Facebook Page public information</p>
                  </div>
                </>
              ) : hasSearched ? (
                <div className="px-3 py-3 text-center text-xs text-warm-grey">
                  No locations found
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SelectedLocation({
  name,
  meta,
  onClear,
}: {
  name: string | null
  meta?: LocationMeta | null
  onClear: () => void
}) {
  return (
    <div className="rounded-md border border-red/20 bg-red/5 px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        {meta?.picture_url ? (
          <img
            src={meta.picture_url}
            alt=""
            className="mt-0.5 size-8 shrink-0 rounded-full border border-stone/20 object-cover"
          />
        ) : (
          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10">
            <MapPinIcon className="size-4 text-red" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-charcoal dark:text-cream">{name}</span>
            {meta?.verified && <VerifiedBadge />}
          </div>
          {meta?.address && (
            <p className="truncate text-[11px] text-warm-grey">{meta.address}</p>
          )}
          {meta?.hours && (
            <p className="truncate text-[11px] text-warm-grey/70">{meta.hours}</p>
          )}
          <p className="mt-1 text-[10px] text-warm-grey/50">Source: Facebook Page public information</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="mt-0.5 shrink-0 rounded-full p-0.5 text-warm-grey transition-colors hover:bg-stone/30 hover:text-charcoal"
        >
          <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}

function VerifiedBadge() {
  return (
    <svg className="size-3.5 shrink-0 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.41 15.06l-4.24-4.24 1.41-1.41 2.83 2.83 6.36-6.36 1.41 1.41-7.77 7.77z" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
    </svg>
  )
}
